// Prediction share link — client library.
//
// Snapshots of the current Search-Force prediction are published to
// the backend as short-code records (POST /api/prediction-snapshot).
// The magician AirDrops `${origin}/n/${code}` to the spectator BEFORE
// the show; the URL renders a stand-alone Notes-style page (see
// /pages/SharedNote.jsx) that mirrors whatever body the app currently
// generates. Any subsequent tweak to Search-Force config or spectator
// answers triggers `syncActiveSnapshot()` which PATCHes the backend
// so the link updates in real time. Once the show is over the
// magician taps Lock — after that the payload is frozen forever, so
// spectators can revisit the link years later and still see the
// exact same note they saw during the performance.

import axios from "axios";
import { STORAGE } from "./storage";
import {
  getSearchForceConfig,
  getSearchForceAnswers,
  getSearchForceArmed,
  getSearchForcePersisted,
  buildSearchForceBody,
  formatSearchForceNoteDate,
  DEFAULT_SEARCH_FORCE_DATE_OFFSET_MINUTES,
} from "./searchForce";
import { readSwiperCard, formatSwiperCard } from "./swiper";
import { getRevealStyle, isCompactReveal } from "./storage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// -------------------------------------------------------------------
// Theme + custom-reveal template getters/setters. These drive which
// UI wrapper the spectator sees at /n/{code}. See STORAGE keys in
// storage.js for the full contract.
// -------------------------------------------------------------------
export const SNAPSHOT_THEMES = ["note", "akinator"];

// Default akinator-mode custom reveal story. Magician overrides via
// Settings. Renders after the spectator taps "Reveal my card" — one
// paragraph per personality trait, then the card name in bold.
export const DEFAULT_CUSTOM_REVEAL =
  "You are a (q1) person at your core.\n\n" +
  "Under pressure, you become (q2).\n\n" +
  "Yet in solitude, you seek (q3).\n\n" +
  "Above all — you are (q4).\n\n" +
  "Someone like you would choose only one card…\n\n" +
  "The (card).";

export function getSnapshotTheme() {
  const v = localStorage.getItem(STORAGE.PREDICTION_LINK_THEME);
  return v && SNAPSHOT_THEMES.includes(v) ? v : "note";
}

export function setSnapshotTheme(theme) {
  if (!SNAPSHOT_THEMES.includes(theme)) return;
  localStorage.setItem(STORAGE.PREDICTION_LINK_THEME, theme);
}

export function getCustomReveal() {
  const v = localStorage.getItem(STORAGE.PREDICTION_LINK_CUSTOM_REVEAL);
  return v == null ? DEFAULT_CUSTOM_REVEAL : v;
}

export function setCustomReveal(template) {
  localStorage.setItem(STORAGE.PREDICTION_LINK_CUSTOM_REVEAL, String(template ?? ""));
}

/** Format the "note date" placeholder identically to how Trick.jsx
 *  renders it. Falls back to `now - offset` when no frozen ISO. */
function computeDatePlaceholder(cfg, langCode) {
  const iso = cfg.dateISO || "";
  let date;
  if (iso) {
    const parsed = new Date(iso);
    date = Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  if (!date) {
    const offsetMin = Number.isFinite(cfg.dateOffsetMinutes)
      ? cfg.dateOffsetMinutes
      : DEFAULT_SEARCH_FORCE_DATE_OFFSET_MINUTES;
    date = new Date(Date.now() - offsetMin * 60 * 1000);
  }
  const locale = (typeof navigator !== "undefined" && navigator.language) || "en-US";
  try {
    return formatSearchForceNoteDate(date, locale, langCode || "");
  } catch {
    return date.toLocaleString(locale);
  }
}

/** Snapshot the current Search-Force + Swiper state into the payload
 *  shape that both /api/prediction-snapshot and SharedNote.jsx /
 *  SharedGenie.jsx expect. `word` is only relevant for the plain
 *  "note" theme; the akinator theme reads name/card/answers from
 *  dedicated payload fields so it can render its own UI. */
export function buildCurrentPayload({ word = "", langCode = "" } = {}) {
  const cfg = getSearchForceConfig();
  const answers = getSearchForceAnswers();
  const armed = getSearchForceArmed();
  const persisted = getSearchForcePersisted();
  const swiperCard = readSwiperCard();

  // ------ Name (xxx) resolution -----------------------------------
  // Priority: explicit `word` param (passed by publish flow) →
  // persisted-note spectator (set when magician searched the name) →
  // empty (akinator will fall back to "friend" placeholder).
  const name = String(word || persisted?.spectator || "").trim();

  // ------ Card resolution -----------------------------------------
  // Priority: Search-Force armed card (lockscreen passcode entry)
  //           → Swiper card (clock-swipe on /notes)
  //           → empty.
  let cardLabel = "";
  let cardSymbol = "";
  if (armed && armed.value) {
    cardLabel = armed.value;
    cardSymbol = armed.symbol || "";
  } else if (swiperCard && swiperCard.value) {
    if (swiperCard.custom) {
      cardLabel = swiperCard.value;
    } else {
      // Full-name format e.g. "seven of hearts"
      cardLabel = formatSwiperCard(swiperCard, "long", null, langCode || "") || swiperCard.value;
      // Compact symbol form e.g. "7♥"
      cardSymbol = formatSwiperCard(swiperCard, "short", null, langCode || "") || "";
    }
  }

  const dateStr = computeDatePlaceholder(cfg, langCode);
  const body = buildSearchForceBody(
    cfg.template,
    name,
    cardLabel,
    dateStr,
    answers,
    "", // dayStr — reserved for future use
    "", // timeStr
    "", // weekdayStr
  );

  return {
    title: cfg.title || "Prediction",
    body,
    date_label: cfg.dateLabel || "",
    date_iso: cfg.dateISO || "",
    reveal_style: getRevealStyle() || "peek",
    backdate_min: parseInt(localStorage.getItem(STORAGE.BACKDATE_MIN) || "0", 10) || 0,
    compact: isCompactReveal(),
    lang: langCode || "",

    // -- Akinator-theme extras. Backend just stores/echoes; the
    //    /n/{code} renderer picks them up when theme==="akinator". --
    theme: getSnapshotTheme(),
    custom_reveal: getCustomReveal(),
    name,
    card_label: cardLabel,
    card_symbol: cardSymbol,
    answers: Array.isArray(answers) ? answers.slice(0, 4) : ["", "", "", ""],
  };
}

/** Read the currently-active snapshot code (or "" if none). */
export function getActiveSnapshotCode() {
  return localStorage.getItem(STORAGE.PREDICTION_LINK_ACTIVE_CODE) || "";
}

export function setActiveSnapshotCode(code) {
  if (code) localStorage.setItem(STORAGE.PREDICTION_LINK_ACTIVE_CODE, code);
  else localStorage.removeItem(STORAGE.PREDICTION_LINK_ACTIVE_CODE);
}

export function isAutoSyncEnabled() {
  const v = localStorage.getItem(STORAGE.PREDICTION_LINK_AUTO_SYNC);
  return v === null ? true : v === "1";
}

export function setAutoSyncEnabled(v) {
  localStorage.setItem(STORAGE.PREDICTION_LINK_AUTO_SYNC, v ? "1" : "0");
}

/** Build the spectator-facing share URL for a given code. Uses the
 *  frontend's own origin (hiddenaether.com in prod) so the URL is
 *  intentionally the same domain as the disguised app — the /n/:code
 *  route lives OUTSIDE the auth Gate. */
export function buildShareUrl(code) {
  if (!code) return "";
  if (typeof window === "undefined") return `/n/${code}`;
  return `${window.location.origin}/n/${code}`;
}

/** Publish a fresh snapshot from the current Search-Force state.
 *  Returns { code, url } on success. Stores the code as active so
 *  future config edits auto-sync into it. */
export async function publishSnapshot({ label = "", langCode = "" } = {}) {
  const payload = buildCurrentPayload({ langCode });
  const res = await axios.post(`${API}/prediction-snapshot`, {
    label,
    payload,
  });
  const code = res.data?.code;
  if (code) {
    setActiveSnapshotCode(code);
  }
  return { code, url: buildShareUrl(code), snapshot: res.data };
}

/** PATCH the active snapshot with the current state. Silent no-op
 *  when no active code, auto-sync is disabled, or the snapshot is
 *  already locked (409 from server). Debounced by caller. */
export async function syncActiveSnapshot({ langCode = "" } = {}) {
  const code = getActiveSnapshotCode();
  if (!code) return null;
  if (!isAutoSyncEnabled()) return null;
  const payload = buildCurrentPayload({ langCode });
  try {
    const res = await axios.patch(`${API}/prediction-snapshot/${code}`, { payload });
    return res.data;
  } catch (e) {
    // 404 → snapshot got deleted server-side; clear active.
    // 409 → snapshot is locked; stop trying until unlocked.
    if (e?.response?.status === 404) {
      setActiveSnapshotCode("");
    }
    return null;
  }
}

let syncTimer = null;
/** Debounced version of syncActiveSnapshot. Multiple settings toggles
 *  in quick succession coalesce into a single backend call. */
export function scheduleSync({ delayMs = 400, langCode = "" } = {}) {
  if (syncTimer) clearTimeout(syncTimer);
  syncTimer = setTimeout(() => {
    syncTimer = null;
    syncActiveSnapshot({ langCode });
  }, delayMs);
}

export async function lockSnapshot(code) {
  const res = await axios.post(`${API}/prediction-snapshot/${code}/lock`, {});
  return res.data;
}

export async function unlockSnapshot(code) {
  const res = await axios.post(`${API}/prediction-snapshot/${code}/unlock`, { confirm: true });
  return res.data;
}

export async function deleteSnapshot(code) {
  await axios.delete(`${API}/prediction-snapshot/${code}`);
  const active = getActiveSnapshotCode();
  if (active === code) setActiveSnapshotCode("");
}

export async function listSnapshots() {
  const res = await axios.get(`${API}/prediction-snapshots`);
  return Array.isArray(res.data) ? res.data : [];
}

export async function fetchSnapshot(code) {
  const res = await axios.get(`${API}/prediction-snapshot/${code}`);
  return res.data;
}
